import React, { useState, useEffect, useRef } from 'react';
import axiosInstance from '../utils/axiosConfig';
import { getApiUrl } from '../utils/config';
import ExportButton from './ExportButton';
import { exportData, exportRenderedTableData } from '../utils/exportUtils';
import { normalizeUser } from '../utils/userUtils';
import LoadingSpinner from './LoadingSpinner';
import '../styles/AdminResourceCenter.css';

const modalBackdropStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.75)',
  backdropFilter: 'blur(12px)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 9999,
  padding: '1rem',
  animation: 'modalFadeIn 0.3s ease-out',
};

const metadataModalStyle = {
  background: 'white',
  borderRadius: '24px',
  maxWidth: '1100px',
  maxHeight: '95vh',
  width: '90%',
  display: 'flex',
  flexDirection: 'column',
  boxShadow: '0 25px 60px -12px rgba(0, 0, 0, 0.4)',
  overflow: 'hidden',
  animation: 'modalSlideIn 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
};

const modernInput = {
  border: '2px solid #e2e8f0',
  borderRadius: 12,
  padding: '1rem 1.25rem',
  fontSize: 15,
  fontFamily: 'Inter, sans-serif',
  marginBottom: 12,
  width: '100%',
  background: '#fff',
  transition: 'all 0.3s ease',
  outline: 'none',
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.02)',
};

const modernButton = {
  background: 'linear-gradient(135deg, #2b4663 0%, #4a90e2 100%)',
  color: '#fff',
  border: 'none',
  borderRadius: 12,
  padding: '1rem 2.5rem',
  fontWeight: 600,
  fontSize: 16,
  letterSpacing: 0.5,
  cursor: 'pointer',
  boxShadow: '0 4px 15px rgba(43, 70, 99, 0.4)',
  transition: 'all 0.3s ease',
  outline: 'none',
  position: 'relative',
  overflow: 'hidden',
};

const modernButtonSecondary = {
  background: '#fff',
  color: '#2b4663',
  border: '2px solid #2b4663',
  borderRadius: 12,
  padding: '1rem 2.5rem',
  fontWeight: 600,
  fontSize: 16,
  letterSpacing: 0.5,
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  outline: 'none',
  boxShadow: '0 2px 8px rgba(43, 70, 99, 0.1)',
};

const downloadButton = {
  background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)',
  color: '#fff',
  border: 'none',
  borderRadius: 8,
  padding: '0.6rem 1.2rem',
  fontWeight: 600,
  fontSize: 14,
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  outline: 'none',
  boxShadow: '0 3px 10px rgba(255, 107, 107, 0.3)',
};

const urlButton = {
  background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
  color: '#fff',
  border: 'none',
  borderRadius: 8,
  padding: '0.6rem 1.2rem',
  fontWeight: 600,
  fontSize: 14,
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  outline: 'none',
  boxShadow: '0 3px 10px rgba(79, 172, 254, 0.3)',
};

const viewDetailsButton = {
  background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
  color: '#2d3748',
  border: 'none',
  borderRadius: 8,
  padding: '0.6rem 1.2rem',
  fontWeight: 600,
  fontSize: 14,
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  outline: 'none',
  boxShadow: '0 3px 10px rgba(168, 237, 234, 0.3)',
};

const selectedTagsStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 8,
  marginTop: 8,
};

const tagStyle = {
  background: 'linear-gradient(135deg, #2b4663 0%, #4a90e2 100%)',
  color: '#fff',
  padding: '0.3rem 0.8rem',
  borderRadius: 16,
  fontSize: 12,
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  boxShadow: '0 2px 8px rgba(43, 70, 99, 0.3)',
};

const removeTagButton = {
  background: 'none',
  border: 'none',
  color: '#fff',
  cursor: 'pointer',
  fontSize: 14,
  fontWeight: 'bold',
  padding: 0,
  margin: 0,
};

const modernCheckbox = {
  appearance: 'none',
  width: '20px',
  height: '20px',
  border: '2px solid #2b4663',
  borderRadius: '4px',
  backgroundColor: '#fff',
  cursor: 'pointer',
  position: 'relative',
  transition: 'all 0.2s ease',
  margin: 0,
  padding: 0,
};

const modernCheckboxChecked = {
  ...modernCheckbox,
  backgroundColor: '#2b4663',
  borderColor: '#2b4663',
};

const checkboxLabel = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  fontWeight: 500,
  color: '#2b4663',
  cursor: 'pointer',
  fontSize: '15px',
};

const checkboxContainer = {
  display: 'flex',
  alignItems: 'center',
  gap: 16,
  padding: '12px 16px',
  background: '#f8f9fa',
  borderRadius: '8px',
  border: '1px solid #e9ecef',
};

const NonAdminResourceCenter = ({ userType, userData }) => {
  // Metadata modal state
  const [showMetadataModal, setShowMetadataModal] = useState(false);
  const [selectedResource, setSelectedResource] = useState(null);
  const [uploaderDetails, setUploaderDetails] = useState(null);
  const [loadingUploaderDetails, setLoadingUploaderDetails] = useState(false);

  // Resource list state
  const [resources, setResources] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedSpecialities, setSelectedSpecialities] = useState([]);
  const [selectedInstitutions, setSelectedInstitutions] = useState([]);
  const [includeGeneralResources, setIncludeGeneralResources] = useState(true);
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(['name', 'file', 'url', 'audience', 'rank']);
  const columnSelectorRef = useRef(null);

  // Available options state
  const [availableSpecialities, setAvailableSpecialities] = useState([]);
  const [availableInstitutions, setAvailableInstitutions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  const RESOURCE_COLUMNS = [
    { key: 'name', label: 'Name' },
    { key: 'file', label: 'File' },
    { key: 'url', label: 'URL' },
    { key: 'text', label: 'Text' },
    { key: 'audience', label: 'Audience' },
    { key: 'rank', label: 'Rank' },
    { key: 'speciality', label: 'Speciality' },
    { key: 'institution', label: 'Institution' }
  ];

  // Get page title based on user type
  const getPageTitle = () => {
    if (userType === 'preceptor') {
      return 'Preceptor Resource Center';
    } else if (userType === 'student') {
      return 'Student Resource Center';
    }
    return 'Resource Center';
  };

  // Handle export functionality
  const handleExport = (format) => {
    try {
      // Export exactly what's currently displayed in the table
      const renderedResources = resources.map(resource => ({
        name: resource.name || resource.filename || 'N/A',
        file: resource.file_id ? 'Available' : 'N/A',
        url: resource.url ? 'Available' : 'N/A',
        text: resource.text ? 'Available' : 'N/A',
        target_audience: resource.target_audience || 'N/A',
        rank: resource.rank || 'N/A',
        specialityTags: Array.isArray(resource.specialityTags) && resource.specialityTags.length > 0 
          ? resource.specialityTags.join(', ') 
          : 'None',
        institutionTags: Array.isArray(resource.institutionTags) && resource.institutionTags.length > 0 
          ? resource.institutionTags.join(', ') 
          : 'None'
      }));

      const columns = [
        { key: 'name', label: 'Name' },
        { key: 'file', label: 'File' },
        { key: 'url', label: 'URL' },
        { key: 'text', label: 'Text' },
        { key: 'target_audience', label: 'Audience' },
        { key: 'rank', label: 'Rank' },
        { key: 'specialityTags', label: 'Speciality' },
        { key: 'institutionTags', label: 'Institution' }
      ];

      const title = `${getPageTitle()} - Resources`;
      const filename = `${getPageTitle().toLowerCase().replace(/\s+/g, '_')}_resources_${new Date().toISOString().split('T')[0]}`;
      
      // Use the new export function for rendered data
      exportRenderedTableData(renderedResources, columns, title, filename, format);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export failed. Please try again.');
    }
  };

  // Fetch available specialities and institutions
  const fetchOptions = async () => {
    setLoadingOptions(true);
    try {
      const [specialitiesRes, institutionsRes] = await Promise.all([
        axiosInstance.get("/crud/get_all_specialities"),
        axiosInstance.get("/crud/get_all_institutions")
      ]);
      
      // Handle both object and string formats for specialities
      const specialities = specialitiesRes.data || [];
      const processedSpecialities = specialities.map(item => {
        if (typeof item === 'string') {
          return item;
        } else if (item && typeof item === 'object') {
          return item.speciality || item.name || item._id || JSON.stringify(item);
        }
        return String(item);
      });
      setAvailableSpecialities(processedSpecialities);
      
      // Handle both object and string formats for institutions
      const institutions = institutionsRes.data || [];
      const processedInstitutions = institutions.map(item => {
        if (typeof item === 'string') {
          return item;
        } else if (item && typeof item === 'object') {
          return item.institution || item.name || item._id || JSON.stringify(item);
        }
        return String(item);
      });
      setAvailableInstitutions(processedInstitutions);
    } catch (err) {
      console.error("Failed to fetch options:", err);
    } finally {
      setLoadingOptions(false);
    }
  };

  // Fetch uploader details
  const fetchUploaderDetails = async (resource) => {
    if (!resource.uploader_id || !resource.uploader_role) {
      setUploaderDetails(null);
      return;
    }

    setLoadingUploaderDetails(true);
    try {
      let endpoint = "";
      if (resource.uploader_role === "preceptor") {
        endpoint = `/crud/get_preceptor_data/${resource.uploader_id}`;
      } else if (resource.uploader_role === "admin") {
        endpoint = `/crud/get_standalone_admin_data/${resource.uploader_id}`;
      } else {
        setUploaderDetails(null);
        return;
      }

      const response = await axiosInstance.get(endpoint);
      setUploaderDetails(normalizeUser(response.data || {}));
    } catch (err) {
      console.error("Failed to fetch uploader details:", err);
      setUploaderDetails(null);
    } finally {
      setLoadingUploaderDetails(false);
    }
  };

  useEffect(() => {
    fetchOptions();
  }, []);

  useEffect(() => {
    const closeOnOutside = (event) => {
      if (columnSelectorRef.current && !columnSelectorRef.current.contains(event.target)) {
        setShowColumnSelector(false);
      }
    };
    document.addEventListener('mousedown', closeOnOutside);
    return () => document.removeEventListener('mousedown', closeOnOutside);
  }, []);

  // Fetch resources - no view_as_role or view_as_rank parameters
  const fetchResources = async () => {
    setLoading(true);
    setError("");
    try {
      const params = {
        page,
        page_size: pageSize,
        include_general_resources: includeGeneralResources,
      };
      if (selectedSpecialities.length > 0) params.specialityTags = selectedSpecialities;
      if (selectedInstitutions.length > 0) params.institutionTags = selectedInstitutions;
      const res = await axiosInstance.get("/crud/resources/", { params });
      setResources(res.data.resources);
      setTotalPages(res.data.pages);
    } catch (err) {
      setError("Failed to load resources.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchResources();
    // eslint-disable-next-line
  }, [page, includeGeneralResources, selectedSpecialities, selectedInstitutions]);

  // Download resource
  const handleDownload = async (resource) => {
    try {
      const response = await axiosInstance.get(`/crud/resources/${resource._id}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', resource.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
      alert('Download failed. Please try again.');
    }
  };

  // Handle resource row click
  const handleResourceClick = async (resource) => {
    setSelectedResource(resource);
    setShowMetadataModal(true);
    setUploaderDetails(null); // Reset uploader details
    await fetchUploaderDetails(resource);
  };

  // Handle multi-select changes
  const handleSpecialityChange = (e) => {
    const value = e.target.value;
    if (value && !selectedSpecialities.includes(value)) {
      setSelectedSpecialities([...selectedSpecialities, value]);
    }
  };

  const handleInstitutionChange = (e) => {
    const value = e.target.value;
    if (value && !selectedInstitutions.includes(value)) {
      setSelectedInstitutions([...selectedInstitutions, value]);
    }
  };

  const removeSpeciality = (speciality) => {
    setSelectedSpecialities(selectedSpecialities.filter(s => s !== speciality));
  };

  const removeInstitution = (institution) => {
    setSelectedInstitutions(selectedInstitutions.filter(i => i !== institution));
  };

  // Pagination
  const handlePrev = () => setPage((p) => Math.max(1, p - 1));
  const handleNext = () => setPage((p) => Math.min(totalPages === 0 ? 1 : totalPages, p + 1));

  // Filter reload
  const handleReload = () => {
    setPage(1);
    fetchResources();
  };

  // Clear filters
  const clearFilters = () => {
    setSelectedSpecialities([]);
    setSelectedInstitutions([]);
    setPage(1);
  };

  // Get uploader display name
  const getUploaderDisplayName = (resource) => {
    if (uploaderDetails) {
      const firstName = uploaderDetails.firstname || '';
      const lastName = uploaderDetails.lastname || '';
      return `${firstName} ${lastName}`.trim() || 'N/A';
    }
    return 'N/A';
  };

  // Get uploader role display
  const getUploaderRoleDisplay = (resource) => {
    if (resource.uploader_role === "preceptor") {
      return "Preceptor, Admin";
    }
    return resource.uploader_role || 'N/A';
  };

  const renderResourceCell = (resource, columnKey) => {
    switch (columnKey) {
      case 'name':
        return <span style={{ fontWeight: 600, fontSize: 14 }}>{resource.name || resource.filename || 'N/A'}</span>;
      case 'file':
        return resource.file_id ? (
          <a href={getApiUrl(`api/resources/download/${encodeURIComponent(resource.filename)}`)} style={{ color: '#0f3a63', fontWeight: 600, textDecoration: 'none' }} onClick={(e) => e.stopPropagation()} target="_blank" rel="noopener noreferrer">Available</a>
        ) : <span style={{ color: '#64748b' }}>N/A</span>;
      case 'url':
        return resource.url ? (
          <a href={resource.url} style={{ color: '#0f3a63', fontWeight: 600, textDecoration: 'none' }} onClick={(e) => e.stopPropagation()} target="_blank" rel="noopener noreferrer">Available</a>
        ) : <span style={{ color: '#64748b' }}>N/A</span>;
      case 'text':
        return resource.text ? <span style={{ color: '#111827', fontWeight: 600 }}>Available</span> : <span style={{ color: '#64748b' }}>N/A</span>;
      case 'audience':
        return <span style={{ textTransform: 'capitalize' }}>{resource.target_audience || 'N/A'}</span>;
      case 'rank':
        return <span style={{ textTransform: 'uppercase' }}>{resource.rank || 'N/A'}</span>;
      case 'speciality':
        return (resource.specialityTags || []).length > 0 ? resource.specialityTags.join(', ') : 'None';
      case 'institution':
        return (resource.institutionTags || []).length > 0 ? resource.institutionTags.join(', ') : 'None';
      default:
        return 'N/A';
    }
  };

  return (
    <div style={{ 
      padding: 'clamp(1rem, 2.5vw, 2rem)',
      minHeight: '100vh',
      width: 'min(1400px, 100%)',
      margin: '0 auto',
      overflow: 'hidden',
      background: 'transparent'
    }}>
      <style>
        {`
          @keyframes modalFadeIn {
            from {
              opacity: 0;
            }
            to {
              opacity: 1;
            }
          }
          
          @keyframes modalSlideIn {
            from {
              transform: translateY(-50px) scale(0.95);
              opacity: 0;
            }
            to {
              transform: translateY(0) scale(1);
              opacity: 1;
            }
          }
          
          @keyframes slideIn {
            from {
              transform: translateX(100%);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }
          
          @keyframes fadeIn {
            from {
              opacity: 0;
            }
            to {
              opacity: 1;
            }
          }
          
          @keyframes slideInScale {
            from {
              transform: translateY(-30px) scale(0.95);
              opacity: 0;
            }
            to {
              transform: translateY(0) scale(1);
              opacity: 1;
            }
          }
          
          @keyframes spin {
            from {
              transform: rotate(0deg);
            }
            to {
              transform: rotate(360deg);
            }
          }
          
          .modern-checkbox:checked::after {
            content: '✓';
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            color: white;
            font-size: 14px;
            font-weight: bold;
            line-height: 1;
          }
          
          .modern-input:focus {
            border-color: #2b4663 !important;
            box-shadow: 0 0 0 4px rgba(43, 70, 99, 0.1) !important;
          }
          
          .modern-button:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(43, 70, 99, 0.4);
          }
          
          .modern-button:active {
            transform: translateY(0);
          }
          
          .url-button:hover {
            transform: scale(1.05) !important;
            box-shadow: 0 4px 15px rgba(79, 172, 254, 0.4) !important;
          }
          
          .download-button:hover {
            transform: scale(1.05) !important;
            box-shadow: 0 4px 15px rgba(255, 107, 107, 0.4) !important;
          }
        `}
      </style>

      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: 32,
        background: 'linear-gradient(135deg, #2b4663 0%, #4a90e2 100%)',
        padding: '2rem',
        borderRadius: 20,
        boxShadow: '0 8px 32px rgba(43, 70, 99, 0.3)',
        color: '#fff'
      }}>
        <div>
          <h2 style={{ 
            margin: 0, 
            fontWeight: 800, 
            letterSpacing: 1.2,
            fontSize: 28,
            color: '#ffffff',
            display: 'flex',
            alignItems: 'center',
            gap: 12
          }}>
            🗂️ {getPageTitle()}
          </h2>
          <p style={{ 
            margin: '8px 0 0 0', 
            opacity: 0.9, 
            fontSize: 16,
            fontWeight: 400
          }}>
            Access and explore educational resources designed for you
          </p>
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <ExportButton 
            onExport={handleExport}
            formats={['PDF', 'CSV']}
            disabled={loading || resources.length === 0}
            customLabel="Export Resources"
          />
        </div>
      </div>

      {/* Metadata Modal - Detailed Resource View */}
      {showMetadataModal && selectedResource && (
        <div style={modalBackdropStyle} onClick={() => setShowMetadataModal(false)}>
          <div style={metadataModalStyle} onClick={e => e.stopPropagation()}>
            {/* Header with dark blue gradient */}
            <div style={{
              background: 'linear-gradient(135deg, #2b4663 0%, #4a90e2 100%)',
              padding: '2.5rem',
              color: 'white',
              position: 'relative'
            }}>
              <button
                onClick={() => setShowMetadataModal(false)}
                style={{
                  position: 'absolute',
                  top: '1.5rem',
                  right: '1.5rem',
                  background: 'none',
                  border: 'none',
                  color: 'rgba(255, 255, 255, 0.9)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  transition: 'all 0.3s ease',
                  fontSize: '20px'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                  e.target.style.color = 'white';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'none';
                  e.target.style.color = 'rgba(255, 255, 255, 0.9)';
                }}
              >
                ×
              </button>
              
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📋</div>
                <h2 style={{ 
                  margin: 0, 
                  fontSize: '2rem', 
                  fontWeight: 700,
                  marginBottom: '0.5rem'
                }}>
                  {selectedResource.name || selectedResource.filename || 'Resource Details'}
                </h2>
                <p style={{ 
                  margin: 0, 
                  fontSize: '1.1rem', 
                  opacity: 0.9,
                  fontWeight: 400
                }}>
                  Complete resource information and metadata
                </p>
              </div>
            </div>

            {/* Content */}
            <div style={{ 
              padding: '2.5rem',
              flex: 1,
              overflowY: 'auto'
            }}>
              {/* Basic Information Section */}
              <div style={{
                background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                borderRadius: '16px',
                padding: '2rem',
                border: '2px solid rgba(43, 70, 99, 0.08)',
                marginBottom: '2rem'
              }}>
                <h3 style={{ 
                  margin: '0 0 1.5rem 0', 
                  color: '#1e293b', 
                  fontWeight: 700,
                  fontSize: '1.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  ℹ️ Basic Information
                </h3>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1.5rem' }}>
                  <div>
                    <label style={{ 
                      fontWeight: 600, 
                      color: '#1e293b', 
                      display: 'block', 
                      marginBottom: '8px',
                      fontSize: '15px'
                    }}>
                      Resource Name
                    </label>
                    <div style={{
                      background: 'white',
                      border: '2px solid #e5e7eb',
                      borderRadius: '12px',
                      padding: '14px 16px',
                      fontSize: '15px',
                      fontWeight: 500,
                      color: '#374151'
                    }}>
                      {selectedResource.name || selectedResource.filename || 'N/A'}
                    </div>
                  </div>
                  
                  <div>
                    <label style={{ 
                      fontWeight: 600, 
                      color: '#1e293b', 
                      display: 'block', 
                      marginBottom: '8px',
                      fontSize: '15px'
                    }}>
                      Description
                    </label>
                    <div style={{
                      background: 'white',
                      border: '2px solid #e5e7eb',
                      borderRadius: '12px',
                      padding: '14px 16px',
                      fontSize: '15px',
                      fontWeight: 500,
                      color: '#374151',
                      minHeight: '60px',
                      wordWrap: 'break-word'
                    }}>
                      {selectedResource.description || 'No description provided'}
                    </div>
                  </div>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1.5rem' }}>
                    <div>
                      <label style={{ 
                        fontWeight: 600, 
                        color: '#1e293b', 
                        display: 'block', 
                        marginBottom: '8px',
                        fontSize: '15px'
                      }}>
                        Upload Date
                      </label>
                      <div style={{
                        background: 'white',
                        border: '2px solid #e5e7eb',
                        borderRadius: '12px',
                        padding: '14px 16px',
                        fontSize: '15px',
                        fontWeight: 500,
                        color: '#374151'
                      }}>
                        {selectedResource.upload_date ? new Date(selectedResource.upload_date).toLocaleDateString() : 'Unknown'}
                      </div>
                    </div>
                    
                    <div>
                      <label style={{ 
                        fontWeight: 600, 
                        color: '#1e293b', 
                        display: 'block', 
                        marginBottom: '8px',
                        fontSize: '15px'
                      }}>
                        Uploader
                      </label>
                      <div style={{
                        background: 'white',
                        border: '2px solid #e5e7eb',
                        borderRadius: '12px',
                        padding: '14px 16px',
                        fontSize: '15px',
                        fontWeight: 500,
                        color: '#374151'
                      }}>
                        {selectedResource.uploader_name || 'Unknown'}
                      </div>
                    </div>
                    
                    <div>
                      <label style={{ 
                        fontWeight: 600, 
                        color: '#1e293b', 
                        display: 'block', 
                        marginBottom: '8px',
                        fontSize: '15px'
                      }}>
                        Content Type
                      </label>
                      <div style={{
                        background: 'white',
                        border: '2px solid #e5e7eb',
                        borderRadius: '12px',
                        padding: '14px 16px',
                        fontSize: '15px',
                        fontWeight: 500,
                        color: '#374151'
                      }}>
                        {selectedResource.content_type || 'Unknown'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Resource Content Section */}
              <div style={{
                background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
                borderRadius: '16px',
                padding: '2rem',
                border: '2px solid rgba(59, 130, 246, 0.15)',
                marginBottom: '2rem'
              }}>
                <h3 style={{ 
                  margin: '0 0 1.5rem 0', 
                  color: '#1e293b', 
                  fontWeight: 700,
                  fontSize: '1.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  📁 Content & Access
                </h3>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1.5rem' }}>
                  {/* File Information */}
                  <div>
                    <label style={{ 
                      fontWeight: 600, 
                      color: '#1e293b', 
                      display: 'block', 
                      marginBottom: '8px',
                      fontSize: '15px'
                    }}>
                      📎 File
                    </label>
                    {selectedResource.file_id ? (
                      <div style={{
                        background: 'white',
                        border: '2px solid #e5e7eb',
                        borderRadius: '12px',
                        padding: '14px 16px',
                        fontSize: '15px',
                        fontWeight: 500,
                        color: '#374151',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px'
                      }}>
                        <span>{selectedResource.filename}</span>
                        <a 
                          href={getApiUrl(`api/resources/download/${encodeURIComponent(selectedResource.filename)}`)}
                          style={{
                            background: 'linear-gradient(135deg, #2b4663 0%, #4a90e2 100%)',
                            color: 'white',
                            textDecoration: 'none',
                            padding: '6px 12px',
                            borderRadius: '8px',
                            fontSize: '13px',
                            fontWeight: 600,
                            marginLeft: 'auto'
                          }}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          📥 Download
                        </a>
                      </div>
                    ) : (
                      <div style={{
                        background: '#f1f5f9',
                        border: '2px solid #e5e7eb',
                        borderRadius: '12px',
                        padding: '14px 16px',
                        fontSize: '15px',
                        color: '#64748b',
                        fontStyle: 'italic'
                      }}>
                        No file attached
                      </div>
                    )}
                  </div>
                  
                  {/* URL Information */}
                  <div>
                    <label style={{ 
                      fontWeight: 600, 
                      color: '#1e293b', 
                      display: 'block', 
                      marginBottom: '8px',
                      fontSize: '15px'
                    }}>
                      🔗 URL
                    </label>
                    {selectedResource.url ? (
                      <div style={{
                        background: 'white',
                        border: '2px solid #e5e7eb',
                        borderRadius: '12px',
                        padding: '14px 16px',
                        fontSize: '15px',
                        fontWeight: 500,
                        color: '#374151',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px'
                      }}>
                        <span style={{ wordBreak: 'break-all', flex: 1 }}>{selectedResource.url}</span>
                        <a 
                          href={selectedResource.url}
                          style={{
                            background: 'linear-gradient(135deg, #2b4663 0%, #4a90e2 100%)',
                            color: 'white',
                            textDecoration: 'none',
                            padding: '6px 12px',
                            borderRadius: '8px',
                            fontSize: '13px',
                            fontWeight: 600
                          }}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          🌐 Visit
                        </a>
                      </div>
                    ) : (
                      <div style={{
                        background: '#f1f5f9',
                        border: '2px solid #e5e7eb',
                        borderRadius: '12px',
                        padding: '14px 16px',
                        fontSize: '15px',
                        color: '#64748b',
                        fontStyle: 'italic'
                      }}>
                        No URL attached
                      </div>
                    )}
                  </div>
                  
                  {/* Text Content */}
                  <div>
                    <label style={{ 
                      fontWeight: 600, 
                      color: '#1e293b', 
                      display: 'block', 
                      marginBottom: '8px',
                      fontSize: '15px'
                    }}>
                      📝 Text Content
                    </label>
                    {selectedResource.text ? (
                      <div style={{
                        background: 'white',
                        border: '2px solid #e5e7eb',
                        borderRadius: '12px',
                        padding: '14px 16px',
                        fontSize: '15px',
                        fontWeight: 500,
                        color: '#374151',
                        maxHeight: '200px',
                        overflowY: 'auto',
                        whiteSpace: 'pre-wrap',
                        wordWrap: 'break-word'
                      }}>
                        {selectedResource.text}
                      </div>
                    ) : (
                      <div style={{
                        background: '#f1f5f9',
                        border: '2px solid #e5e7eb',
                        borderRadius: '12px',
                        padding: '14px 16px',
                        fontSize: '15px',
                        color: '#64748b',
                        fontStyle: 'italic'
                      }}>
                        No text content
                      </div>
                    )}
                  </div>
                  
                  {!selectedResource.file_id && !selectedResource.url && !selectedResource.text && (
                    <div style={{ 
                      background: '#fef2f2', 
                      border: '2px solid #ef4444', 
                      borderRadius: '12px',
                      padding: '1.5rem',
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>⚠️</div>
                      <div style={{ fontWeight: 600, color: '#dc2626', marginBottom: '0.5rem' }}>No Content Available</div>
                      <div style={{ fontSize: '14px', color: '#b91c1c' }}>
                        This resource doesn't have any files, URLs, or text content attached.
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Targeting Section */}
              <div style={{
                background: 'linear-gradient(135deg, #fef7ff 0%, #faf5ff 100%)',
                borderRadius: '16px',
                padding: '2rem',
                border: '2px solid rgba(147, 51, 234, 0.15)',
                marginBottom: '2rem'
              }}>
                <h3 style={{ 
                  margin: '0 0 1.5rem 0', 
                  color: '#1e293b', 
                  fontWeight: 700,
                  fontSize: '1.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  🎯 Targeting & Access
                </h3>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                  <div>
                    <label style={{ 
                      fontWeight: 600, 
                      color: '#1e293b', 
                      display: 'block', 
                      marginBottom: '8px',
                      fontSize: '15px'
                    }}>
                      Target Audience
                    </label>
                    <div style={{
                      background: 'white',
                      border: '2px solid #e5e7eb',
                      borderRadius: '12px',
                      padding: '14px 16px',
                      fontSize: '15px',
                      fontWeight: 500,
                      color: '#374151'
                    }}>
                      {selectedResource.target_audience?.charAt(0).toUpperCase() + selectedResource.target_audience?.slice(1) || 'Not specified'}
                    </div>
                  </div>
                  
                  <div>
                    <label style={{ 
                      fontWeight: 600, 
                      color: '#1e293b', 
                      display: 'block', 
                      marginBottom: '8px',
                      fontSize: '15px'
                    }}>
                      Rank
                    </label>
                    <div style={{
                      background: 'white',
                      border: '2px solid #e5e7eb',
                      borderRadius: '12px',
                      padding: '14px 16px',
                      fontSize: '15px',
                      fontWeight: 500,
                      color: '#374151'
                    }}>
                      {selectedResource.rank ? (selectedResource.rank === 'ALL' ? 'All Ranks' : 
                       selectedResource.rank === 'FIRST' ? 'First Year' :
                       selectedResource.rank === 'SECOND' ? 'Second Year' :
                       selectedResource.rank === 'THIRD' ? 'Third Year' :
                       selectedResource.rank === 'FOURTH' ? 'Fourth Year' :
                       selectedResource.rank === 'FIFTH' ? 'Fifth Year' :
                       selectedResource.rank === 'SIXTH' ? 'Sixth Year' : selectedResource.rank) : 'Not specified'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Tags Section */}
              <div style={{
                background: 'linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%)',
                borderRadius: '16px',
                padding: '2rem',
                border: '2px solid rgba(34, 197, 94, 0.15)',
                marginBottom: '2rem'
              }}>
                <h3 style={{ 
                  margin: '0 0 1.5rem 0', 
                  color: '#1e293b', 
                  fontWeight: 700,
                  fontSize: '1.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  🏷️ Tags & Categories
                </h3>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                  {/* Speciality Tags */}
                  <div>
                    <label style={{ 
                      fontWeight: 600, 
                      color: '#1e293b', 
                      display: 'block', 
                      marginBottom: '8px',
                      fontSize: '15px'
                    }}>
                      Speciality Tags
                    </label>
                    <div style={{
                      background: 'white',
                      border: '2px solid #e5e7eb',
                      borderRadius: '12px',
                      padding: '14px 16px',
                      minHeight: '50px',
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '8px',
                      alignItems: 'flex-start'
                    }}>
                      {selectedResource.specialityTags && selectedResource.specialityTags.length > 0 ? (
                        selectedResource.specialityTags.map((speciality, index) => (
                          <span key={`details-speciality-${index}-${speciality}`} style={{
                            background: 'linear-gradient(135deg, #2b4663 0%, #4a90e2 100%)',
                            color: 'white',
                            padding: '6px 12px',
                            borderRadius: '8px',
                            fontSize: '13px',
                            fontWeight: 600,
                            boxShadow: '0 4px 12px rgba(43, 70, 99, 0.3)'
                          }}>
                            {speciality}
                          </span>
                        ))
                      ) : (
                        <span style={{ color: '#9ca3af', fontStyle: 'italic', fontSize: '14px' }}>
                          No speciality tags
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Institution Tags */}
                  <div>
                    <label style={{ 
                      fontWeight: 600, 
                      color: '#1e293b', 
                      display: 'block', 
                      marginBottom: '8px',
                      fontSize: '15px'
                    }}>
                      Institution Tags
                    </label>
                    <div style={{
                      background: 'white',
                      border: '2px solid #e5e7eb',
                      borderRadius: '12px',
                      padding: '14px 16px',
                      minHeight: '50px',
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '8px',
                      alignItems: 'flex-start'
                    }}>
                      {selectedResource.institutionTags && selectedResource.institutionTags.length > 0 ? (
                        selectedResource.institutionTags.map((institution, index) => (
                          <span key={`details-institution-${index}-${institution}`} style={{
                            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                            color: 'white',
                            padding: '6px 12px',
                            borderRadius: '8px',
                            fontSize: '13px',
                            fontWeight: 600,
                            boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
                          }}>
                            {institution}
                          </span>
                        ))
                      ) : (
                        <span style={{ color: '#9ca3af', fontStyle: 'italic', fontSize: '14px' }}>
                          No institution tags
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* System & Uploader Information */}
              <div style={{
                background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
                borderRadius: '16px',
                padding: '2rem',
                border: '2px solid rgba(245, 158, 11, 0.15)'
              }}>
                <h3 style={{ 
                  margin: '0 0 1.5rem 0', 
                  color: '#1e293b', 
                  fontWeight: 700,
                  fontSize: '1.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  ⚙️ System Information
                </h3>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                  <div>
                    <label style={{ 
                      fontWeight: 600, 
                      color: '#1e293b', 
                      display: 'block', 
                      marginBottom: '8px',
                      fontSize: '15px'
                    }}>
                      Resource ID
                    </label>
                    <div style={{
                      background: 'white',
                      border: '2px solid #e5e7eb',
                      borderRadius: '12px',
                      padding: '14px 16px',
                      fontSize: '13px',
                      fontWeight: 500,
                      color: '#6b7280',
                      fontFamily: 'ui-monospace, monospace',
                      wordBreak: 'break-all'
                    }}>
                      {selectedResource._id || 'N/A'}
                    </div>
                  </div>
                  
                  {selectedResource.file_id && (
                    <div>
                      <label style={{ 
                        fontWeight: 600, 
                        color: '#1e293b', 
                        display: 'block', 
                        marginBottom: '8px',
                        fontSize: '15px'
                      }}>
                        File ID
                      </label>
                      <div style={{
                        background: 'white',
                        border: '2px solid #e5e7eb',
                        borderRadius: '12px',
                        padding: '14px 16px',
                        fontSize: '13px',
                        fontWeight: 500,
                        color: '#6b7280',
                        fontFamily: 'ui-monospace, monospace',
                        wordBreak: 'break-all'
                      }}>
                        {selectedResource.file_id}
                      </div>
                    </div>
                  )}
                  
                  <div>
                    <label style={{ 
                      fontWeight: 600, 
                      color: '#1e293b', 
                      display: 'block', 
                      marginBottom: '8px',
                      fontSize: '15px'
                    }}>
                      Uploader Role
                    </label>
                    <div style={{
                      background: 'white',
                      border: '2px solid #e5e7eb',
                      borderRadius: '12px',
                      padding: '14px 16px',
                      fontSize: '15px',
                      fontWeight: 500,
                      color: '#374151'
                    }}>
                      {getUploaderRoleDisplay(selectedResource)}
                    </div>
                  </div>
                  
                  {loadingUploaderDetails && (
                    <div style={{ 
                      gridColumn: '1 / -1', 
                      color: '#64748b', 
                      fontStyle: 'italic', 
                      textAlign: 'center',
                      padding: '1rem'
                    }}>
                      🔄 Loading additional uploader details...
                    </div>
                  )}
                  
                  {uploaderDetails && (
                    <>
                      {uploaderDetails.email && (
                        <div>
                          <label style={{ 
                            fontWeight: 600, 
                            color: '#1e293b', 
                            display: 'block', 
                            marginBottom: '8px',
                            fontSize: '15px'
                          }}>
                            Uploader Email
                          </label>
                          <div style={{
                            background: 'white',
                            border: '2px solid #e5e7eb',
                            borderRadius: '12px',
                            padding: '14px 16px',
                            fontSize: '15px',
                            fontWeight: 500,
                            color: '#374151'
                          }}>
                            {uploaderDetails.email}
                          </div>
                        </div>
                      )}
                      
                      {uploaderDetails.specialityId && (
                        <div>
                          <label style={{ 
                            fontWeight: 600, 
                            color: '#1e293b', 
                            display: 'block', 
                            marginBottom: '8px',
                            fontSize: '15px'
                          }}>
                            Department
                          </label>
                          <div style={{
                            background: 'white',
                            border: '2px solid #e5e7eb',
                            borderRadius: '12px',
                            padding: '14px 16px',
                            fontSize: '15px',
                            fontWeight: 500,
                            color: '#374151'
                          }}>
                            {uploaderDetails.specialityId}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div style={{ 
                display: 'flex', 
                gap: '1rem', 
                paddingTop: '2rem',
                borderTop: '2px solid #f1f5f9',
                justifyContent: 'center',
                flexWrap: 'wrap'
              }}>
                {selectedResource.file_id && (
                  <button 
                    style={{
                      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      color: 'white',
                      border: 'none',
                      padding: '14px 24px',
                      borderRadius: '12px',
                      fontSize: '15px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                      transition: 'all 0.3s ease'
                    }}
                    onClick={() => handleDownload(selectedResource)}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 6px 20px rgba(16, 185, 129, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.3)';
                    }}
                  >
                    📥 Download File
                  </button>
                )}
                
                {selectedResource.url && (
                  <button 
                    style={{
                      background: 'linear-gradient(135deg, #2b4663 0%, #4a90e2 100%)',
                      color: 'white',
                      border: 'none',
                      padding: '14px 24px',
                      borderRadius: '12px',
                      fontSize: '15px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      boxShadow: '0 4px 12px rgba(43, 70, 99, 0.3)',
                      transition: 'all 0.3s ease'
                    }}
                    onClick={() => window.open(selectedResource.url, '_blank', 'noopener,noreferrer')}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 6px 20px rgba(43, 70, 99, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 4px 12px rgba(43, 70, 99, 0.3)';
                    }}
                  >
                    🌐 Visit URL
                  </button>
                )}
                
                <button 
                  style={{
                    background: 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
                    color: 'white',
                    border: 'none',
                    padding: '14px 24px',
                    borderRadius: '12px',
                    fontSize: '15px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    boxShadow: '0 4px 12px rgba(107, 114, 128, 0.3)',
                    transition: 'all 0.3s ease'
                  }}
                  onClick={() => setShowMetadataModal(false)}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 20px rgba(107, 114, 128, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 12px rgba(107, 114, 128, 0.3)';
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div style={{ 
        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)', 
        borderRadius: 20, 
        boxShadow: '0 8px 32px rgba(43, 70, 99, 0.1)', 
        padding: 32, 
        marginBottom: 32,
        border: '1px solid rgba(43, 70, 99, 0.1)'
      }}>
        <h3 style={{ 
          color: '#2b4663', 
          fontWeight: 700, 
          marginTop: 0, 
          marginBottom: 24,
          fontSize: 24,
          display: 'flex',
          alignItems: 'center',
          gap: 12
        }}>
          🔍 Filters & Search
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 24 }}>
          <div>
            <label style={{ 
              fontWeight: 600, 
              color: '#2b4663', 
              display: 'block', 
              marginBottom: 12,
              fontSize: 15
            }}>
              Speciality Tags:
            </label>
            <select 
              value="" 
              onChange={handleSpecialityChange} 
              style={{
                ...modernInput,
                background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                border: '2px solid #e2e8f0',
                borderRadius: 12,
                padding: '14px 16px',
                fontSize: 15,
                fontWeight: 500
              }}
              disabled={loadingOptions}
            >
              <option value="">Select specialities...</option>
              {availableSpecialities.map((speciality, index) => (
                <option key={`speciality-${index}-${speciality}`} value={speciality}>
                  {speciality}
                </option>
              ))}
            </select>
            {selectedSpecialities.length > 0 && (
              <div style={{...selectedTagsStyle, marginTop: 12}}>
                {selectedSpecialities.map((speciality, index) => (
                  <span key={`selected-speciality-${index}-${speciality}`} style={{
                    ...tagStyle,
                    background: 'linear-gradient(135deg, #2b4663 0%, #4a90e2 100%)',
                    boxShadow: '0 4px 12px rgba(43, 70, 99, 0.3)',
                    fontSize: 13,
                    padding: '6px 12px'
                  }}>
                    {speciality}
                    <button 
                      style={removeTagButton}
                      onClick={() => removeSpeciality(speciality)}
                      title="Remove"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div>
            <label style={{ 
              fontWeight: 600, 
              color: '#2b4663', 
              display: 'block', 
              marginBottom: 12,
              fontSize: 15
            }}>
              Institution Tags:
            </label>
            <select 
              value="" 
              onChange={handleInstitutionChange} 
              style={{
                ...modernInput,
                background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                border: '2px solid #e2e8f0',
                borderRadius: 12,
                padding: '14px 16px',
                fontSize: 15,
                fontWeight: 500
              }}
              disabled={loadingOptions}
            >
              <option value="">Select institutions...</option>
              {availableInstitutions.map((institution, index) => (
                <option key={`institution-${index}-${institution}`} value={institution}>
                  {institution}
                </option>
              ))}
            </select>
            {selectedInstitutions.length > 0 && (
              <div style={{...selectedTagsStyle, marginTop: 12}}>
                {selectedInstitutions.map((institution, index) => (
                  <span key={`selected-institution-${index}-${institution}`} style={{
                    ...tagStyle,
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                    fontSize: 13,
                    padding: '6px 12px'
                  }}>
                    {institution}
                    <button 
                      style={removeTagButton}
                      onClick={() => removeInstitution(institution)}
                      title="Remove"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            padding: '20px',
            background: 'linear-gradient(135deg, #f0f8ff 0%, #e0f2fe 100%)',
            borderRadius: 16,
            border: '2px solid rgba(59, 130, 246, 0.1)'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12
            }}>
              <input 
                type="checkbox" 
                id="include-general-resources"
                checked={includeGeneralResources} 
                onChange={e => setIncludeGeneralResources(e.target.checked)}
                style={includeGeneralResources ? modernCheckboxChecked : modernCheckbox}
                className="modern-checkbox"
              />
              <label 
                htmlFor="include-general-resources"
                style={{
                  fontWeight: 600,
                  color: '#2b4663',
                  fontSize: 15,
                  cursor: 'pointer',
                  userSelect: 'none'
                }}
              >
                Include General Resources
              </label>
              <button 
                type="button"
                style={{
                  background: 'rgba(59, 130, 246, 0.1)',
                  color: '#3b82f6',
                  border: '1px solid rgba(59, 130, 246, 0.2)',
                  borderRadius: '50%',
                  width: '24px',
                  height: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
                title="Include general resources means ones with no speciality nor institution tags - they are available to everyone"
              >
                i
              </button>
            </div>
            <div style={{
              fontSize: 13,
              color: '#64748b',
              fontStyle: 'italic',
              lineHeight: 1.4
            }}>
              General resources have no speciality or institution tags and are available to everyone
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 16, marginTop: 32, justifyContent: 'center' }}>
          <button 
            onClick={handleReload} 
            style={{
              ...modernButton,
              background: 'linear-gradient(135deg, #2b4663 0%, #4a90e2 100%)',
              padding: '14px 32px',
              fontSize: 16,
              fontWeight: 600,
              borderRadius: 12,
              boxShadow: '0 6px 20px rgba(43, 70, 99, 0.3)'
            }}
          >
            🔍 Apply Filters
          </button>
          <button 
            onClick={clearFilters} 
            style={{
              ...modernButtonSecondary,
              padding: '14px 32px',
              fontSize: 16,
              fontWeight: 600,
              borderRadius: 12
            }}
          >
            🗑️ Clear Filters
          </button>
        </div>
      </div>

      {/* Resource List */}
      <div style={{ 
        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)', 
        borderRadius: 20, 
        boxShadow: '0 8px 32px rgba(43, 70, 99, 0.1)', 
        padding: 32, 
        marginBottom: 32,
        border: '1px solid rgba(43, 70, 99, 0.1)'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 32
        }}>
          <h3 style={{ 
            color: '#2b4663', 
            fontWeight: 700, 
            margin: 0,
            fontSize: 24,
            display: 'flex',
            alignItems: 'center',
            gap: 12
          }}>
            Resource Library
          </h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div className="column-selector-container" ref={columnSelectorRef}>
              <button type="button" className="column-selector-button" onClick={() => setShowColumnSelector((prev) => !prev)}>
                Customize Columns
              </button>
              {showColumnSelector && (
                <div className="column-selector-dropdown">
                  <div className="column-selector-header"><h4>Visible Columns</h4></div>
                  {RESOURCE_COLUMNS.map((col) => (
                    <label key={col.key} className="column-option">
                      <input
                        type="checkbox"
                        checked={visibleColumns.includes(col.key)}
                        disabled={visibleColumns.length === 1 && visibleColumns.includes(col.key)}
                        onChange={() => {
                          setVisibleColumns((prev) =>
                            prev.includes(col.key) ? prev.filter((k) => k !== col.key) : [...prev, col.key]
                          );
                        }}
                      />
                      <span>{col.label}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            <ExportButton 
              onExport={handleExport}
              formats={['PDF', 'CSV']}
              disabled={loading || resources.length === 0}
              customLabel="Export Resources"
            />
          </div>
        </div>
        {loading ? (
          <div style={{ padding: '40px 0' }}>
            <LoadingSpinner text="Loading resources..." variant="inline" />
          </div>
        ) : error ? (
          <div style={{ 
            color: '#ef4444', 
            background: '#fee2e2', 
            padding: 24, 
            borderRadius: 12, 
            textAlign: 'center',
            fontSize: 16,
            fontWeight: 500
          }}>
            {error}
          </div>
        ) : (
          <>
            <div style={{ width: '100%', overflowX: 'auto' }}>
              <table style={{ 
                width: '100%', 
                borderCollapse: 'separate', 
                borderSpacing: 0, 
                marginBottom: 24, 
                borderRadius: 16, 
                overflow: 'hidden', 
                background: '#fff', 
                tableLayout: 'auto',
                boxShadow: '0 8px 32px rgba(43, 70, 99, 0.1)',
                border: '1px solid rgba(43, 70, 99, 0.1)',
                minWidth: `${Math.max(visibleColumns.length * 180, 920)}px`
              }}>
                <thead>
                  <tr style={{ background: 'linear-gradient(140deg, rgba(10, 30, 52, 0.96) 0%, rgba(21, 45, 72, 0.92) 100%)' }}>
                    {RESOURCE_COLUMNS.filter((col) => visibleColumns.includes(col.key)).map((col) => (
                      <th key={col.key} style={{ padding: 20, borderBottom: 'none', color: '#fff', fontWeight: 700, fontSize: 15, textAlign: 'left', minWidth: 160 }}>
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {resources.length === 0 ? (
                    <tr>
                      <td colSpan={visibleColumns.length} style={{ 
                        textAlign: 'center', 
                        padding: 60, 
                        color: '#64748b',
                        fontSize: 18,
                        fontWeight: 500
                      }}>
                        <div style={{ fontSize: 48, marginBottom: 16, opacity: 0.5 }}>📂</div>
                        No resources found.
                      </td>
                    </tr>
                  ) : resources.map((r, idx) => (
                    <tr key={idx} 
                        style={{ 
                          cursor: 'pointer', 
                          transition: 'all 0.3s ease',
                          background: idx % 2 === 0 ? '#fff' : '#f8fafc',
                          borderBottom: '1px solid #f1f5f9'
                        }} 
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'linear-gradient(135deg, #e0f2fe 0%, #f0f9ff 100%)';
                          e.currentTarget.style.transform = 'scale(1.005)';
                          e.currentTarget.style.boxShadow = '0 4px 20px rgba(43, 70, 99, 0.1)';
                        }} 
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = idx % 2 === 0 ? '#fff' : '#f8fafc';
                          e.currentTarget.style.transform = 'scale(1)';
                          e.currentTarget.style.boxShadow = 'none';
                        }} 
                        onClick={() => handleResourceClick(r)}
                    >
                      {RESOURCE_COLUMNS.filter((col) => visibleColumns.includes(col.key)).map((col) => (
                        <td key={col.key} style={{ padding: 16, minWidth: 160 }}>
                          {renderResourceCell(r, col.key)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Pagination */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              gap: 20, 
              marginTop: 24 
            }}>
              <button 
                onClick={handlePrev} 
                disabled={page === 1 || totalPages <= 1}
                style={{
                  ...modernButtonSecondary,
                  padding: '12px 24px',
                  fontSize: 14,
                  fontWeight: 600,
                  opacity: page === 1 || totalPages <= 1 ? 0.5 : 1,
                  cursor: page === 1 || totalPages <= 1 ? 'not-allowed' : 'pointer'
                }}
              >
                ← Previous
              </button>
              <span style={{ 
                color: '#2b4663', 
                fontWeight: 600, 
                fontSize: 16,
                padding: '12px 24px',
                background: 'linear-gradient(135deg, #f0f8ff 0%, #e0f2fe 100%)',
                borderRadius: 12,
                border: '1px solid rgba(43, 70, 99, 0.1)'
              }}>
                Page {totalPages === 0 ? 1 : page} of {totalPages === 0 ? 1 : totalPages}
              </span>
              <button 
                onClick={handleNext} 
                disabled={page === (totalPages === 0 ? 1 : totalPages) || totalPages <= 1}
                style={{
                  ...modernButtonSecondary,
                  padding: '12px 24px',
                  fontSize: 14,
                  fontWeight: 600,
                  opacity: page === (totalPages === 0 ? 1 : totalPages) || totalPages <= 1 ? 0.5 : 1,
                  cursor: page === (totalPages === 0 ? 1 : totalPages) || totalPages <= 1 ? 'not-allowed' : 'pointer'
                }}
              >
                Next →
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default NonAdminResourceCenter; 
